import React, { useEffect, useState } from "react";
import AOS from "aos";
import API from "../api";
import SEO from "../components/SEO";
import Hero from "../components/Hero";
import Partners from "../components/Partners";
import "swiper/css";
import "swiper/css/pagination";

const slidesData = [
    {
        id: 1,
        image: "/assets/img/banner1.png",
        title: "About Us",
        subtitle:
            "As a pan-African university, our passion is to respond to the actual and realistic needs of students who want to empower themselves, and through empowering themselves, contribute to wealth creation and development in Africa.",
        buttonText: "Apply Now",
        buttonLink: "/courses",
    },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";
const DEFAULT_PROSPECTUS_URL = "/assets/uploads/prospectus.pdf";

function resolveDocumentUrl(filePath) {
    if (!filePath || filePath.startsWith("http") || filePath.startsWith("/assets/")) {
        return filePath;
    }
    return `${API_BASE_URL}${filePath}`;
}

const AboutPage = () => {
    const [prospectusUrl, setProspectusUrl] = useState(DEFAULT_PROSPECTUS_URL);

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    useEffect(() => {
        let isMounted = true;

        async function loadProspectus() {
            try {
                const res = await API.get("/site-documents/prospectus");
                const filePath = res.data?.data?.file_path;
                if (!isMounted || !filePath) return;

                setProspectusUrl(resolveDocumentUrl(filePath));
            } catch (err) {
                console.error("Unable to load prospectus document:", err);
            }
        }

        loadProspectus();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, behavior: "auto" });
        }
    }, []);

    return (
        <section className="pt-1 pb-16 bg-white">
            <SEO 
                title="About — Africa Research University" 
                description="Learn about Africa Research University, our vision, mission and programmes."
                url="/about"
                breadcrumbs={[
                    { name: 'Home', url: 'https://africaresearchuniversity.com' },
                    { name: 'About', url: 'https://africaresearchuniversity.com/about' }
                ]}
            />
            {/* Hero */}
            <Hero
                slides={slidesData}
                interval={6000}
                height="420px"
                borderRadius="50px"
                margin="60px"
                hidden={true}
            />

            {/* About Section */}
            <section className="relative py-20 overflow-hidden">
                {/* background accents */}
                <div className="absolute -top-24 -right-24 w-80 h-80 bg-green-200/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 -left-24 w-80 h-80 bg-lime-200/20 rounded-full blur-3xl pointer-events-none" />

                <div className="container mx-auto px-4 grid md:grid-cols-2 gap-14 items-center relative z-10">
                    
                    {/* Image */}
                    <div data-aos="fade-up" data-aos-delay="100">
                        <div className="relative rounded-[28px] overflow-hidden shadow-xl group">
                            <img
                                src="/assets/img/prog-2.jpg"
                                alt="About Africa Research University"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                        </div>
                    </div>

                    {/* Content */}
                    <div data-aos="fade-up" data-aos-delay="200">

                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-snug">
                            We are committed to improving services to students — making
                            education more accessible, enjoyable, and affordable.
                        </h3>

                        <div className="space-y-5 text-gray-700 leading-relaxed mb-8">
                            <p>
                                Africa Research University (ARU) is a private open distance-learning
                                tertiary institution with a rapidly growing student base and an
                                expanding portfolio of academic programmes.
                            </p>

                            <p>
                                Our programmes include Bachelor’s Degrees in Business
                                Administration, Masters of Philosophy in Development Studies, 
                                Doctorate of Philosophy in Development Studies,
                                Masters of Public Health, Masters of Public Administration and more.
                            </p>

                            <p>
                                A comprehensive process is underway to make all courses
                                available online — enabling flexible, high-quality learning
                                across borders.
                            </p>
                        </div>

                        {/* Vision Card */}
                        <div className="relative p-6 rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-lime-50 shadow-sm mb-8">
                            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-green-400 to-lime-400 rounded-t-2xl" />
                            <h4 className="text-lg font-semibold text-green-800 mb-2">
                                Our Vision
                            </h4>
                            <p className="text-gray-700 leading-relaxed">
                                To become the leading provider of quality distance education
                                throughout Sub-Saharan Africa and beyond. We currently support
                                over <strong>2,000 students</strong> across Zambia, Zimbabwe,
                                Uganda, South Africa, and the Democratic Republic of the Congo,
                                with expansion into Nigeria and Kenya underway.
                            </p>
                        </div>

                        {/* Primary CTA */}
                        <a
                            href="/courses"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-green-500 to-lime-400 text-black font-semibold shadow-md hover:opacity-90 transition"
                        >
                            Explore our programmes →
                        </a>
                    </div>
                </div>
            </section>

            {/* Prospectus CTA (Secondary) */}
            <div className="text-center py-16 bg-white" data-aos="fade-up">
                <a
                    href={prospectusUrl}
                    download
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-xl border border-green-500 text-green-700 font-semibold hover:bg-green-50 transition"
                >
                    Download Prospectus
                </a>
            </div>

            {/* Partners (Grounded, clean finish) */}
            <div className="pt-12">
                <Partners />
            </div>
        </section>
    );
};

export default AboutPage;
